import { useEffect, useRef } from 'react';

/**
 * Pure helper that decides how fast (and which direction) a scroll container
 * should scroll while the user drags the pointer near its top or bottom edge.
 *
 * The container has two "hot zones" of height `threshold` px — one at the top
 * and one at the bottom. When the pointer is inside a hot zone, we scroll; the
 * deeper into the zone (closer to the edge) the pointer is, the faster we go.
 * Outside both zones (the comfortable middle of the container) we return 0 so
 * the page stays still.
 *
 * Returning a plain number keeps every bit of the edge/ramp/clamp maths unit
 * testable without needing a real DOM, layout, or requestAnimationFrame.
 *
 * @param {number} clientY   Pointer Y position in viewport coordinates.
 * @param {DOMRect} rect     The scroll container's bounding rect.
 * @param {number} threshold Height in px of each edge hot zone.
 * @param {number} maxSpeed  Maximum scroll distance per animation frame (px).
 * @returns {number} Pixels to add to scrollTop this frame: negative = scroll
 *                   up, positive = scroll down, 0 = do not scroll.
 */
export function computeAutoScrollDelta(clientY, rect, threshold, maxSpeed) {
  if (!rect || threshold <= 0 || maxSpeed <= 0) return 0;

  // Ignore positions that are not vertically over the container at all — we
  // never want to keep scrolling once the drag has left the scrollable area.
  if (clientY < rect.top || clientY > rect.bottom) return 0;

  const distanceFromTop = clientY - rect.top;
  const distanceFromBottom = rect.bottom - clientY;

  // Top hot zone — scroll up. Intensity ramps from 0 at the inner edge of the
  // zone to 1 right at the container's top edge.
  if (distanceFromTop < threshold) {
    const intensity = (threshold - distanceFromTop) / threshold;
    return -maxSpeed * Math.min(Math.max(intensity, 0), 1);
  }

  // Bottom hot zone — scroll down.
  if (distanceFromBottom < threshold) {
    const intensity = (threshold - distanceFromBottom) / threshold;
    return maxSpeed * Math.min(Math.max(intensity, 0), 1);
  }

  return 0;
}

/**
 * useDragAutoScroll — auto-scrolls a container while an HTML5 drag is in
 * progress and the pointer hovers near the container's top or bottom edge.
 *
 * Why this exists: clinicians drag notes (and probing-question / domain chips)
 * around the smart-notes pages. Native HTML5 drag-and-drop does NOT scroll the
 * page for you, so without this a clinician had to drop the item at the very
 * edge and re-grab it repeatedly to reach an off-screen target. This hook makes
 * the page glide while they hold a drag near an edge, just like a file manager.
 *
 * How it works:
 *   - We listen for `dragover` on the container to track the pointer's Y.
 *   - A requestAnimationFrame loop nudges `scrollTop` every frame by the amount
 *     computed by `computeAutoScrollDelta`, giving smooth, frame-rate-independent
 *     scrolling even when the pointer is held still near the edge (where no
 *     further `dragover` events fire).
 *   - We stop on `drop`, on `dragend` (fires on the drag source no matter where
 *     it ends), and when the pointer leaves the container, so we never scroll
 *     after the drag is over or once the pointer has moved away.
 *
 * It is intentionally generic (works for any HTML5 drag type) and side-effect
 * free beyond the listeners/animation frame it owns and cleans up.
 *
 * @param {React.RefObject<HTMLElement>} scrollRef Ref to the scrollable element.
 * @param {{ threshold?: number, maxSpeed?: number }} [options]
 *        threshold — edge hot-zone height in px (default 64).
 *        maxSpeed  — max px scrolled per frame (default 18, ~1000px/s at 60fps).
 */
export function useDragAutoScroll(scrollRef, options = {}) {
  const { threshold = 64, maxSpeed = 18 } = options;

  // Held in refs so the animation loop reads live values without re-binding
  // listeners on every render.
  const frameRef = useRef(null);
  const deltaRef = useRef(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    const step = () => {
      if (deltaRef.current !== 0) {
        // Browsers clamp scrollTop at 0 and (scrollHeight - clientHeight), so we
        // do not need to guard the bounds ourselves.
        el.scrollTop += deltaRef.current;
      }
      frameRef.current = requestAnimationFrame(step);
    };

    const startLoop = () => {
      if (frameRef.current == null) {
        frameRef.current = requestAnimationFrame(step);
      }
    };

    const stopLoop = () => {
      if (frameRef.current != null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      deltaRef.current = 0;
    };

    const handleDragOver = (e) => {
      const rect = el.getBoundingClientRect();
      deltaRef.current = computeAutoScrollDelta(e.clientY, rect, threshold, maxSpeed);
      if (deltaRef.current !== 0) startLoop();
    };

    // When the pointer leaves the container for somewhere outside it, halt —
    // dragover stops firing so a stale non-zero delta would otherwise keep
    // scrolling. Moving onto a child element keeps relatedTarget inside, so we
    // ignore those (the bubbled dragover will keep the delta fresh).
    const handleDragLeave = (e) => {
      if (!el.contains(e.relatedTarget)) stopLoop();
    };

    el.addEventListener('dragover', handleDragOver);
    el.addEventListener('dragleave', handleDragLeave);
    el.addEventListener('drop', stopLoop);
    document.addEventListener('dragend', stopLoop);

    return () => {
      el.removeEventListener('dragover', handleDragOver);
      el.removeEventListener('dragleave', handleDragLeave);
      el.removeEventListener('drop', stopLoop);
      document.removeEventListener('dragend', stopLoop);
      stopLoop();
    };
  }, [scrollRef, threshold, maxSpeed]);
}
